import { Box, Card, Group, Text, ThemeIcon, Title } from "@mantine/core";
import { IconChevronRight } from "@tabler/icons-react";
import classes from "./StatCard.module.css";

interface Props {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  onClick?: () => void;
  action?: React.ReactNode;
  /** Emphasizes the tile with a semantic-tinted border + wash (e.g. net income). */
  accent?: "brand" | "green" | "red";
}

/** KPI tile: a brand-tinted icon badge, an uppercase dimmed label, a large
 *  tabular-nums value, a compact sub-line, and a hover lift when clickable. */
export function StatCard({ icon, label, value, sub, onClick, action, accent }: Props) {
  // Local CSS vars drive the accent colors so the wash respects light/dark mode.
  const accentVars = accent
    ? ({
      "--statcard-accent-border": `var(--mantine-color-${accent}-4)`,
      "--statcard-accent-bg"    : `var(--mantine-color-${accent}-0)`
    } as React.CSSProperties)
    : undefined;
  const card = (
    <Card
      withBorder
      radius="md"
      padding="md"
      style={accentVars}
      className={`${classes.card} ${onClick ? classes.clickable : ""} ${accent ? classes.accent : ""}`}
    >
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <ThemeIcon variant="light" color="brand" size={38} radius="md">
          {icon}
        </ThemeIcon>
        {onClick && <IconChevronRight size={16} className={classes.chevron} color="var(--mantine-color-gray-5)" />}
      </Group>
      <Text size="xs" fw={600} c="dimmed" tt="uppercase" mt="sm" style={{ letterSpacing: "0.06em" }}>
        {label}
      </Text>
      <Title order={3} mt={4} style={{ fontVariantNumeric: "tabular-nums" }}>
        {value}
      </Title>
      {sub && <Text size="xs" c="dimmed" mt={4} component="div">{sub}</Text>}
      {action && <Group mt="sm">{action}</Group>}
    </Card>
  );

  if (onClick) {
    // role="button" on a Box (div), not UnstyledButton — the card body may contain
    // an `action` <Button>, and a <button> cannot legally nest another <button>.
    return (
      <Box
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
          }
        }}
        className={classes.button}
      >
        {card}
      </Box>
    );
  }
  return card;
}
